import React, { useState } from 'react';
import { Heart } from 'lucide-react';

/**
 * 🧠 Zero-Friction Onboarding
 * 
 * NO questions. NO feature tour. NO selling.
 * 
 * Just ONE moment of emotional connection, then straight to the app.
 * 
 * "I remember how yesterday felt and help you live today better."
 */
const ZeroFrictionOnboarding = ({ userName, onComplete }) => {
  const [phase, setPhase] = useState('hook'); // 'hook' -> 'promise' -> done

  const handleContinue = async () => {
    if (phase === 'hook') {
      setPhase('promise');
    } else {
      // Save minimal onboarding data
      try {
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/onboarding`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              completed: true,
              completedAt: new Date().toISOString(),
              version: 'zero-friction-v1'
            })
          }
        );
      } catch (error) {
        console.error('Onboarding save error:', error);
      }
      onComplete();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white flex items-center justify-center p-6">
      <div className="max-w-sm w-full text-center">
        
        {/* Phase 1: Emotional Hook */}
        {phase === 'hook' && (
          <div className="animate-fade-in-up">
            {/* Gentle heart */}
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-full flex items-center justify-center">
                <Heart className="w-10 h-10 text-teal-500" fill="currentColor" />
              </div>
            </div>

            {/* The ONE sentence */}
            <h1 className="text-2xl font-medium text-gray-800 leading-relaxed mb-2">
              Hi{userName ? `, ${userName.split(' ')[0]}` : ''}.
            </h1>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              I remember how yesterday felt<br/>
              <span className="text-teal-600">and help you live today better.</span>
            </p>

            <p className="text-sm text-gray-400 mb-8">
              — Allora
            </p>

            <button
              onClick={handleContinue}
              className="text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors"
            >
              Continue →
            </button>
          </div>
        )}

        {/* Phase 2: The Promise (brief) */}
        {phase === 'promise' && (
          <div className="animate-fade-in-up">
            <div className="space-y-6 mb-10">
              <p className="text-gray-600 leading-relaxed">
                I'll learn your patterns over time.
              </p>
              <p className="text-gray-600 leading-relaxed">
                No setup. No configuration.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Just start talking when you're ready.
              </p>
            </div>

            <button
              onClick={handleContinue}
              className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-500 hover:from-teal-700 hover:to-cyan-600 text-white rounded-2xl font-medium shadow-lg shadow-teal-500/25 transition-all"
            >
              Let's begin
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZeroFrictionOnboarding;
